/*
 * Renderer-neutral editable WFS feature set.
 *
 * This module tracks loaded feature items, WFS-T change state, property helpers,
 * and save/reload flow. Rendering adapters provide conversion and collection
 * callbacks for Leaflet, deck.gl, or another frontend layer implementation.
 */
import {Evented} from "@/engine/evented.js";

const STATE_EXIST = "exist";
const STATE_INSERT = "insertElement";
const STATE_UPDATE = "updateElement";
const STATE_REMOVE = "removeElement";

export const WFS_FEATURE_STATES = {
    exist: STATE_EXIST,
    insert: STATE_INSERT,
    update: STATE_UPDATE,
    remove: STATE_REMOVE,
};

export class WfsEditableFeatureSet extends Evented {
    /**
     * Creates a renderer-neutral editable feature set.
     * @param {Object} options Feature-set dependencies.
     * @param {import("@/engine/wfst/wfsTransactionalClient.js").WfsTransactionalClient} options.wfst WFS-T transport client.
     * @param {Function} options.getItemId Returns a stable id for one renderer item.
     * @param {Function} options.featureToItem Converts a neutral WFS feature to a renderer item.
     * @param {Function} options.itemToFeature Converts a renderer item to a neutral WFS feature.
     * @param {Function} options.addItem Adds a renderer item to the concrete collection.
     * @param {Function} options.removeItem Removes a renderer item from the concrete collection.
     * @param {Function} [options.prepareLoadedItem] Applies renderer-specific setup to a loaded item.
     * @param {Function} [options.onItemLoadError] Receives per-feature conversion errors.
     */
    constructor(options) {
        super();
        this.wfst = options.wfst;
        this.getItemId = options.getItemId;
        this.featureToItem = options.featureToItem;
        this.itemToFeature = options.itemToFeature;
        this.addItemToCollection = options.addItem;
        this.removeItemFromCollection = options.removeItem;
        this.prepareLoadedItem = options.prepareLoadedItem || function () {};
        this.onItemLoadError = options.onItemLoadError || function () {};
        this.state = WFS_FEATURE_STATES;
        this.changes = {};
    }

    /**
     * Adds an item to the collection and optionally marks it as newly inserted.
     * @param {Object} item Renderer item.
     * @param {Object} [options] Add options.
     * @param {boolean} [options.trackNew=true] Whether an item without state becomes an insert.
     * @returns {Object} Added item.
     */
    addItem(item, options = {}) {
        const trackNew = options.trackNew !== false;
        this.ensureFeatureApi(item);
        this.ensureFeatureShape(item);
        this.addItemToCollection(item);
        if (trackNew && !item.state) {
            item.state = this.state.insert;
            this.changes[this.getItemId(item)] = item;
            this.fire("change:queued", {
                operation: "insert",
                item,
                items: [item],
            });
        }
        return item;
    }

    /**
     * Removes an item from the collection and marks persisted items for deletion.
     * @param {Object} item Renderer item.
     * @returns {Object} Removed item.
     */
    removeItem(item) {
        this.removeItemFromCollection(item);
        const id = this.getItemId(item);
        if (id in this.changes) {
            const change = this.changes[id];
            if (change.state === this.state.insert) {
                delete this.changes[id];
                this.fire("change:cleared", {
                    operation: "insert",
                    item,
                    items: [item],
                });
            } else {
                change.state = this.state.remove;
                this.fire("change:queued", {
                    operation: "delete",
                    item,
                    items: [item],
                });
            }
        } else {
            item.state = this.state.remove;
            this.changes[id] = item;
            this.fire("change:queued", {
                operation: "delete",
                item,
                items: [item],
            });
        }
        return item;
    }

    /**
     * Marks an item as changed for the next WFS-T transaction.
     * @param {Object} item Renderer item.
     * @returns {Object} Edited item.
     */
    editItem(item) {
        this.ensureFeatureApi(item);
        this.ensureFeatureShape(item);
        if (item.state !== this.state.insert) {
            item.state = this.state.update;
        }
        this.changes[this.getItemId(item)] = item;
        this.fire("change:queued", {
            operation: item.state === this.state.insert ? "insert" : "update",
            item,
            items: [item],
        });
        return item;
    }

    /**
     * Loads WFS features, converts them to renderer items, and adds them as existing items.
     * @param {import("ol/format/filter/Filter.js").default|Array<string>|null} [filter] Optional filter or ids.
     * @returns {Promise<Array<Object>>} Loaded renderer items.
     */
    async loadFeatures(filter = null) {
        let features;
        try {
            features = Array.isArray(filter)
                ? await this.wfst.loadFeaturesByIds(filter.map((item) => item?.id || item?.value || item))
                : await this.wfst.loadFeatures(filter);
        } catch (error) {
            this.fire("load:failed", {
                error,
                filter,
            });
            throw error;
        }
        const items = [];
        for (const feature of features) {
            try {
                const item = this.featureToItem(feature);
                if (!item) {
                    this.fire("feature:skip", {
                        feature,
                        reason: "empty",
                    });
                    continue;
                }
                item.state = this.state.exist;
                this.ensureFeatureApi(item);
                this.ensureFeatureShape(item);
                this.prepareLoadedItem(item, feature);
                this.addItem(item, {trackNew: false});
                items.push(item);
            } catch (error) {
                this.onItemLoadError(error, feature);
                this.fire("feature:skip", {
                    feature,
                    error,
                    reason: "conversion-error",
                });
            }
        }
        this.fire("load", {
            features,
            items,
        });
        return items;
    }

    /**
     * Saves all pending WFS-T changes and reloads inserted items by server ids.
     * @returns {Promise<{inserted: Array<Object>, updated: Array<Object>, deleted: Array<Object>, insertedLoaded: Array<Object>, transactionResponse: Object}>} Save result.
     */
    async save() {
        const changedItems = Object.values(this.changes);
        const inserted = changedItems.filter((item) => item.state === this.state.insert);
        const updated = changedItems.filter((item) => item.state === this.state.update);
        const deleted = changedItems.filter((item) => item.state === this.state.remove);
        if (changedItems.length === 0) {
            return {inserted, updated, deleted, insertedLoaded: [], transactionResponse: {}};
        }

        let result;
        try {
            result = await this.wfst.saveChanges({
                inserts: inserted.map((item) => this.itemToFeatureForSave(item)),
                updates: updated.map((item) => this.itemToFeatureForSave(item)),
                deletes: deleted.map((item) => this.itemToFeatureForSave(item)),
            });
        } catch (error) {
            this.fire("save:failed", {
                error,
                inserted,
                updated,
                deleted,
            });
            throw error;
        }
        const insertedLoaded = await this.applyTransactionSuccess(result.transactionResponse, inserted, updated, deleted);
        const saveResult = {
            inserted,
            updated,
            deleted,
            insertedLoaded,
            transactionResponse: result.transactionResponse,
        };
        this.fire("save:success", saveResult);
        return saveResult;
    }

    /**
     * Applies transaction summary state changes to tracked items.
     * @param {Object} response Parsed WFS transaction response.
     * @param {Array<Object>} inserted Inserted items.
     * @param {Array<Object>} updated Updated items.
     * @param {Array<Object>} deleted Deleted items.
     * @returns {Promise<Array<Object>>} Items reloaded after insert.
     */
    async applyTransactionSuccess(response, inserted, updated, deleted) {
        const summary = response.transactionSummary || {};
        if (updated.length > 0 && summary.totalUpdated === updated.length) {
            for (const item of updated) {
                item.state = this.state.exist;
                delete this.changes[this.getItemId(item)];
            }
            this.fire("feature:update", {
                operation: "update",
                items: updated,
                features: updated.map((item) => this.itemToFeatureForSave(item)),
                transactionResponse: response,
            });
        }
        if (deleted.length > 0 && summary.totalDeleted === deleted.length) {
            for (const item of deleted) {
                item.state = null;
                delete this.changes[this.getItemId(item)];
            }
            this.fire("feature:delete", {
                operation: "delete",
                items: deleted,
                features: deleted.map((item) => this.itemToFeatureForSave(item)),
                transactionResponse: response,
            });
        }
        if (inserted.length === 0 || summary.totalInserted <= 0) {
            return [];
        }

        for (const item of inserted) {
            this.removeItemFromCollection(item);
            delete this.changes[this.getItemId(item)];
        }
        const ids = response.insertIds || [];
        const insertedLoaded = ids.length > 0 ? await this.loadFeatures(ids) : inserted;
        for (const item of insertedLoaded) {
            item.state = this.state.exist;
        }
        this.fire("feature:insert", {
            operation: "insert",
            items: insertedLoaded,
            originalItems: inserted,
            features: insertedLoaded.map((item) => this.itemToFeatureForSave(item)),
            transactionResponse: response,
        });
        return insertedLoaded;
    }

    /**
     * Converts one renderer item to a neutral WFS feature and normalizes geometry for WFS/GML.
     * @param {Object} item Renderer item.
     * @returns {Object} Neutral WFS feature ready for transport.
     */
    itemToFeatureForSave(item) {
        const feature = this.itemToFeature(item);
        if (!feature?.geometry) {
            return feature;
        }

        const closeRing = (ring) => {
            if (ring.length === 0) {
                return ring;
            }
            const first = ring[0];
            const last = ring[ring.length - 1];
            return first[0] === last[0] && first[1] === last[1] ? ring : [...ring, [...first]];
        };

        let coordinates = null;
        if (feature.geometry.type === "Polygon") {
            coordinates = feature.geometry.coordinates.map(closeRing);
        }
        if (feature.geometry.type === "MultiPolygon") {
            coordinates = feature.geometry.coordinates.map((polygon) => polygon.map(closeRing));
        }
        return coordinates ? {
            ...feature,
            geometry: {
                ...feature.geometry,
                coordinates,
            },
        } : feature;
    }

    /**
     * Ensures a renderer item has a feature object with a properties object.
     * @param {Object} item Renderer item.
     */
    ensureFeatureShape(item) {
        if (!item.feature) {
            item.feature = {
                type: "Feature",
                properties: {},
                geometry: null,
            };
        }
        if (!item.feature.properties) {
            item.feature.properties = {};
        }
    }

    /**
     * Adds property helper methods expected by drawing/editing UI code.
     * @param {Object} item Renderer item.
     */
    ensureFeatureApi(item) {
        if (typeof item.setProperties !== "function") {
            item.setProperties = function (obj) {
                if (!this.feature) {
                    this.feature = {
                        type: "Feature",
                        properties: {},
                    };
                }
                if (!this.feature.properties) {
                    this.feature.properties = {};
                }
                Object.assign(this.feature.properties, obj);
            };
        }
        if (typeof item.getProperty !== "function") {
            item.getProperty = function (field) {
                return this.feature?.properties?.[field] ?? null;
            };
        }
        if (typeof item.deleteProperties !== "function") {
            item.deleteProperties = function (fields) {
                if (!this.feature?.properties) {
                    return;
                }
                for (const field of fields) {
                    delete this.feature.properties[field];
                }
            };
        }
    }
}

/**
 * Normalizes GeoJSON geometry and removes empty coordinate branches.
 * @param {Object|null} geometry GeoJSON geometry from WFS.
 * @returns {Object|null} Renderable GeoJSON geometry or null when empty.
 */
export function normalizeWfsGeometry(geometry) {
    if (!geometry || !geometry.type) {
        return null;
    }
    if (geometry.type === "Point") {
        return isValidCoordinate(geometry.coordinates) ? geometry : null;
    }
    if (geometry.type === "MultiPoint") {
        const coordinates = geometry.coordinates.filter(isValidCoordinate);
        return coordinates.length > 0 ? {...geometry, coordinates} : null;
    }
    if (geometry.type === "LineString") {
        return validLine(geometry.coordinates) ? geometry : null;
    }
    if (geometry.type === "MultiLineString") {
        const coordinates = geometry.coordinates.filter(validLine);
        return coordinates.length > 0 ? {...geometry, coordinates} : null;
    }
    if (geometry.type === "Polygon") {
        const coordinates = normalizePolygonCoordinates(geometry.coordinates);
        return coordinates ? {...geometry, coordinates} : null;
    }
    if (geometry.type === "MultiPolygon") {
        const coordinates = geometry.coordinates
            .map(normalizePolygonCoordinates)
            .filter(Boolean);
        return coordinates.length > 0 ? {...geometry, coordinates} : null;
    }
    return null;
}

/**
 * Removes empty rings from one polygon coordinate array.
 * @param {Array} coordinates GeoJSON polygon coordinates.
 * @returns {Array|null} Normalized polygon coordinates or null when empty.
 */
function normalizePolygonCoordinates(coordinates) {
    if (!Array.isArray(coordinates)) {
        return null;
    }
    const rings = coordinates.filter(validRing);
    return rings.length > 0 ? rings : null;
}

/**
 * Checks whether a coordinate is a finite GeoJSON position.
 * @param {Array} coordinate Candidate coordinate.
 * @returns {boolean} True when longitude and latitude are finite numbers.
 */
function isValidCoordinate(coordinate) {
    return Array.isArray(coordinate) &&
        Number.isFinite(coordinate[0]) &&
        Number.isFinite(coordinate[1]);
}

/**
 * Checks whether a line has enough valid coordinates to render.
 * @param {Array} coordinates GeoJSON line coordinates.
 * @returns {boolean} True when the line can be rendered by an adapter.
 */
function validLine(coordinates) {
    return Array.isArray(coordinates) &&
        coordinates.length >= 2 &&
        coordinates.every(isValidCoordinate);
}

/**
 * Checks whether a polygon ring has enough valid coordinates to render.
 * @param {Array} ring GeoJSON ring coordinates.
 * @returns {boolean} True when the ring can be rendered by an adapter.
 */
function validRing(ring) {
    return Array.isArray(ring) &&
        ring.length >= 3 &&
        ring.every(isValidCoordinate);
}
